import * as THREE from "three";

/**
 * Cheap industrial PMREM for metal reflections.
 * Avoids RoomEnvironment's heavy instancing — a few lit panels bake into a soft env map.
 */
export function createIndustrialEnvMap(renderer: THREE.WebGLRenderer): {
  texture: THREE.Texture;
  dispose: () => void;
} {
  const pmrem = new THREE.PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();

  const bake = new THREE.Scene();
  bake.background = new THREE.Color(0x1a1612);

  const hemi = new THREE.HemisphereLight(0xc8b89a, 0x1a120e, 1.2);
  bake.add(hemi);

  const key = new THREE.DirectionalLight(0xffe2c0, 2.2);
  key.position.set(4, 8, 2);
  bake.add(key);

  const fill = new THREE.DirectionalLight(0x6a8090, 0.55);
  fill.position.set(-5, 3, -4);
  bake.add(fill);

  const panelMat = new THREE.MeshBasicMaterial({ color: 0x8a9098, side: THREE.BackSide });
  const warmMat = new THREE.MeshBasicMaterial({ color: 0xb08048, side: THREE.BackSide });
  const coolMat = new THREE.MeshBasicMaterial({ color: 0x4a6070, side: THREE.BackSide });

  const shell = new THREE.Mesh(new THREE.BoxGeometry(12, 12, 12), panelMat);
  bake.add(shell);

  const warm = new THREE.Mesh(new THREE.PlaneGeometry(4, 6), warmMat);
  warm.position.set(5.5, 1, 0);
  warm.rotation.y = -Math.PI / 2;
  bake.add(warm);

  const cool = new THREE.Mesh(new THREE.PlaneGeometry(5, 4), coolMat);
  cool.position.set(-5.5, 0.5, 1);
  cool.rotation.y = Math.PI / 2;
  bake.add(cool);

  const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(12, 12),
    new THREE.MeshBasicMaterial({ color: 0x2a241c, side: THREE.DoubleSide }),
  );
  floor.rotation.x = Math.PI / 2;
  floor.position.y = -5.5;
  bake.add(floor);

  const rt = pmrem.fromScene(bake, 0.04);
  const texture = rt.texture;

  panelMat.dispose();
  warmMat.dispose();
  coolMat.dispose();
  shell.geometry.dispose();
  warm.geometry.dispose();
  cool.geometry.dispose();
  floor.geometry.dispose();
  (floor.material as THREE.Material).dispose();
  pmrem.dispose();

  return {
    texture,
    dispose: () => {
      texture.dispose();
      rt.dispose();
    },
  };
}
